import React, { useEffect, useState } from "react";
import { Button, Col, Row, Spinner, Stack } from "react-bootstrap";
import { useParams } from "react-router-dom";
import {
  fetchBeds,
  fetchEthnicities,
  fetchGenders,
  fetchJobs,
  fetchPrograms,
  fetchRaces,
  fetchRelationships,
  fetchRooms,
  patchBed,
  patchFieldValue,
  patchGuardian,
  postFieldValue,
} from "../../../../services/httpClient";
import DatePicker from "../../../ui/DatePicker";
import Input from "../../../ui/Input";
import InputPhone from "../../../ui/InputPhone";
import SelectObject from "../../../ui/SelectObject";
import ConfirmDelete from "./ConfirmDelete";
import FormCheck from "../../../ui/FormCheckbox";
import ConfirmBan from "../../../ui/ConfirmBan";
import useApp from "../../../../utils/useApp";
import SelectObjectTypeahead from "../../../ui/SelectObjectTypeahead";
import CustomField from "../../../ui/CustomField";
import CaptureProfilePic from "../../../ui/CaptureProfilePic";
import { User } from "../../../../types/user";
import { UserFields } from "../../../../types/userFields";
import { validateUserFields } from "../../../../helpers/validation";

interface EditGuardianState {
  user?: User;
  guardians: any;
  fetchHousehold: any;
  currentCase?: any;
  myCase: boolean;
  isLastUser: boolean;
  resetHouseholdTab: any;
  setUserToSave: any;
  onClickDoneEdit: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const EditGuardian = ({
  user,
  guardians,
  fetchHousehold,
  currentCase,
  myCase,
  isLastUser,
  resetHouseholdTab,
  setUserToSave,
  onClickDoneEdit,
  setShowToast,
  setToastBody,
  setToastText,
}: EditGuardianState) => {
  const { id } = useParams();
  const { auth }: any = useApp();
  const [isPrimaryUpdated, setIsPrimaryUpdated] = useState<boolean>(false);
  const [custom, setCustom] = useState<any[]>([]);
  const [profileImage, setProfileImage] = useState<string>("");
  const [saveIsEnabled, setSaveIsEnabled] = useState<boolean>(false);
  const [userUpdated, setUserUpdated] = useState<boolean>(false);
  const [customUpdated, setCustomUpdated] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState("");
  const [relationships, setRelationships] = useState<any[]>([]);
  const [genders, setGenders] = useState<any[]>([]);
  const [races, setRaces] = useState<any[]>([]);
  const [ethnicities, setEthnicities] = useState<any[]>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [levels, setLevels] = useState<any[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const [rooms, setRooms] = useState<any[]>([]);
  const [beds, setBeds] = useState<any[]>([]);
  const [filteredBeds, setFilteredBeds] = useState<any[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBanning, setIsBanning] = useState(false);
  const [guardianToBan, setGuardianToBan] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [userFields, setUserFields] = useState<UserFields>({
    firstName: "",
    lastName: "",
    preferredName: "",
    email: "",
    phone: null,
    intakeDate: undefined,
    exitDate: undefined,
    birthDate: undefined,
    relationship: "",
    gender: "",
    ethnicity: "",
    race: "",
    program: null,
    level: null,
    previousJob: "",
    job: null,
    room: null,
    previousBed: null,
    bed: null,
    isPrimary: false,
    appAccess: false,
    appAccessLast: undefined,
    isBanned: false,
  });

  useEffect(() => {
    setIsLoading(true);
    getRelationships();
    getGenders();
    getRaces();
    getEthnicities();
    getPrograms();
    getJobs();
    if (auth?.profile.includeHMIS === 1) {
      getBeds();
    }
  }, [user]);

  const getRelationships = () => {
    fetchRelationships().then((res: any) => {
      setRelationships(
        res[0].data.filter((c: any) => {
          return c.key !== "child";
        }),
      );
    });
  };

  const getGenders = () => {
    fetchGenders().then((res: any) => {
      setGenders(res[0].data);
    });
  };

  const getRaces = () => {
    fetchRaces().then((res: any) => {
      setRaces(res[0].data);
    });
  };

  const getEthnicities = () => {
    fetchEthnicities().then((res: any) => {
      setEthnicities(res[0].data);
    });
  };

  const getPrograms = () => {
    fetchPrograms()
      .then((res: any) => {
        let programs = res[0].data;
        programs = [{ id: 0, key: 0, name: "Select from List", programLevels: [] }, ...programs];
        setPrograms(programs);
        setLevels([]);

        const selectedProgram = programs.find((p: any) => p.key === user?.program);
        if (selectedProgram) {
          if (selectedProgram.programLevels && selectedProgram.programLevels.length > 0) {
            setLevels(selectedProgram.programLevels);
            if (user?.level_id) {
              const selectedLevel = selectedProgram.programLevels.find((lvl: any) => lvl.id === user.level_id);
              if (selectedLevel) {
                setUserFields((prev) => ({ ...prev, level: selectedLevel }));
              } else {
                setUserFields((prev) => ({ ...prev, level: null }));
              }
            }
          }
          setUserFields((prev) => ({ ...prev, program: selectedProgram }));
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getJobs = () => {
    fetchJobs().then((res: any) => {
      const jobs = res[0].data;
      var availJobs = jobs.filter((j: any) => j.positions_filled < j.positions_total);

      //Ensure user's job is included
      const userJob = jobs.find((j: any) => j.key === user?.job);

      if (userJob && !availJobs.some((j: any) => j.id === userJob.id)) {
        availJobs.push(userJob);
      }

      availJobs = [{ id: 0, key: 0, name: "Select from List" }, ...availJobs];
      setJobs(availJobs);

      const selectedJob = availJobs.find((j: any) => j.key === user?.job);
      if (selectedJob) {
        setUserFields((prev) => ({ ...prev, job: selectedJob }));
      } else if (user?.job) {
        const currentJob = jobs.find((j: any) => j.key === user?.job);
        setUserFields((prev) => ({ ...prev, job: currentJob }));
        setJobs((prevJobs) => [currentJob, ...prevJobs]);
      } else {
        // setJob({ key: "", name: "" });
      }
    });
  };

  const getBeds = () => {
    fetchBeds()
      .then((res: any) => {
        var bedList = res[0].data;
        //include bed user is currently assigned to
        var currentBed;
        if (user?.bed) {
          currentBed = bedList.find((b: any) => b.key === user?.bed);
        }
        //filter to vacant beds
        bedList = bedList.filter((b: any) => b.occupant_id === null);

        if (currentBed) {
          //if user is assigned to a bed, add it to the list
          bedList.push(currentBed);
        }

        bedList.sort((a: any, b: any) => {
          // group by room name
          if (a.room_name !== b.room_name) {
            return a.room_name.localeCompare(b.room_name, undefined, { numeric: true, sensitivity: "base" });
          }
          // sort beds in each room alphanumerically
          return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
        });

        setBeds(bedList);
        if (user?.room) {
          const filtered = bedList.filter((b: any) => b.room_key === user?.room);
          setFilteredBeds(filtered);
        } else {
          setFilteredBeds(bedList);
        }
        getRooms();
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setErrMsg("");
      });
  };

  const getRooms = () => {
    fetchRooms()
      .then((res: any) => {
        const roomList = res[0].data;
        roomList.sort((a: any, b: any) => {
          return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
        });
        setRooms(roomList);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  useEffect(() => {
    if (!user) return;

    setUserFields({
      firstName: user.firstname ?? "",
      lastName: user.lastname ?? "",
      preferredName: user.preferredname ?? "",
      email: user.email ?? "",
      phone: user.phonenumber === "" ? null : user.phonenumber,
      intakeDate: user.intake_at,
      exitDate: user.exit_at,
      birthDate: user.birthdate,
      relationship: user.relationship ?? "",
      gender: user.gender ?? "",
      ethnicity: user.ethnicity ?? "",
      race: user.race ?? "",
      previousJob: user.job ?? "",
      ...(auth?.profile.includeHMIS === 1 && {
        previousBed: user.bed,
        room: user.room ?? "",
        bed: user.bed ?? null,
      }),
      isPrimary: user.is_primary === 1,
      appAccess: user?.appaccess === 1 ? true : false,
      appAccessLast: user.appaccess_last_at,
      isBanned: user.is_banned === 1,
    });

    if (user.profile.length > 0) {
      user?.profile?.map((p: any) => {
        const base64String = "data:image/jpeg;base64, " + p?.data;
        setProfileImage(base64String);
      });
    } else {
      setProfileImage("");
    }

    if (user.customfields) {
      setCustom(user.customfields);
    }
  }, [user, auth]);

  function validateUser(
    first: string,
    last: string,
    emailVal: any,
    phoneVal: any,
    level: any,
    levelRequired: boolean,
    appAccess: boolean = false,
    fieldType: string = "user",
  ) {
    let errors: string[] = [];
    errors = validateUserFields(first, last, emailVal, phoneVal, level, levelRequired, appAccess);

    if (setUserToSave) setUserToSave(true);
    if (fieldType === "custom") {
      setCustomUpdated(true);
    } else {
      setUserUpdated(true);
    }

    return {
      isValid: errors.length === 0,
      errors: errors,
    };
  }

  const updateField = (field: keyof typeof userFields, value: UserFields[keyof UserFields], levelRequired = levels.length > 0) => {
    setUserFields((prev) => {
      const updated = { ...prev, [field]: value };
      const validation = validateUser(
        updated.firstName,
        updated.lastName,
        updated.email,
        updated.phone,
        updated.level,
        levelRequired,
        updated.appAccess,
      );
      setSaveIsEnabled(validation.isValid);
      if (setUserToSave) setUserToSave(true);
      return updated;
    });
  };

  const updateIsPrimary = () => {
    if (!userFields.isPrimary) {
      setIsPrimaryUpdated(true);
    }
    updateField("isPrimary", !userFields.isPrimary);
  };

  const updateProgram = (e: any) => {
    updateField("level", null);
    let updatedLevels = [];
    setLevels([]);
    if (e.programLevels && e.programLevels.length > 0) {
      updatedLevels = e.programLevels;
    }
    setLevels(updatedLevels);
    updateField("program", e, updatedLevels.length > 0);
  };

  const updateLevel = (e: any) => {
    if (e.key !== userFields.level?.key) {
      updateField("level", e);
    }
  };

  const updateJob = (e: any) => {
    if (e.key !== userFields.job?.key) {
      updateField("previousJob", userFields.job?.key);
      updateField("job", e);
    }
  };

  const updateRoom = (e: any) => {
    updateField("room", e.target.value);
    updateField("bed", null);
    const filtered = beds.filter((b: any) => b.room_key === e.target.value);
    setFilteredBeds(filtered);
  };

  const updateBed = (e: any) => {
    updateField("previousBed", userFields.bed);
    updateField("bed", e.target.value);
  };

  const saveGuardian = () => {
    const validation = validateUser(
      userFields.firstName,
      userFields.lastName,
      userFields.email,
      userFields.phone,
      userFields.level,
      levels.length > 0,
      userFields.appAccess,
    );
    setErrMsg("");

    if (validation.isValid) {
      if (isPrimaryUpdated) {
        const otherGuardians = guardians.filter((g: any) => {
          return g.id !== user?.id;
        });

        const otherUpdatedGuardian = {
          is_primary: false,
        };

        otherGuardians.map((o: any) => {
          patchGuardian(otherUpdatedGuardian, id, o?.id)
            .then((res: any) => {})
            .catch((err: any) => {
              setErrMsg(err?.response?.data?.msg);
            });
          return true;
        });
      }

      if (userUpdated) {
        var updatedGuardian: any = {
          firstname: userFields.firstName,
          lastname: userFields.lastName,
          preferredname: userFields.preferredName,
          is_primary: userFields.isPrimary,
          email: userFields.email,
          phonenumber: userFields.phone === "1" ? null : userFields.phone,
          intake_at: userFields.intakeDate,
          exit_at: userFields.exitDate,
          birthdate: userFields.birthDate,
          relationship: userFields.relationship,
          gender: userFields.gender,
          ethnicity: userFields.ethnicity,
          race: userFields.race,
          program: userFields.program?.key === 0 ? 0 : userFields.program?.key ? userFields.program.key : null,
          level_id: userFields.level?.id === 0 ? 0 : userFields.level?.id ? userFields.level.id : null,
          previous_job: userFields.previousJob,
          // job: userFields.job?.key === 0 ? 0 : userFields.job?.key ? userFields.job.key : null,
        };

        if (userFields.previousJob !== userFields.job?.key) {
          updatedGuardian.job = userFields.job;
        }

        if (updatedGuardian.program === 0) {
          updatedGuardian.level_id = null;
        }

        const prevAppAccess = user?.appaccess === 1 ? true : false;
        if (prevAppAccess !== userFields.appAccess) {
          updatedGuardian.appaccess = userFields.appAccess;
        }

        patchGuardian(updatedGuardian, id, user?.id)
          .then((res: any) => {})
          .catch((err: any) => {
            setErrMsg(err?.response?.data?.msg);
          });

        if (auth?.profile.includeHMIS === 1) {
          if (userFields.bed != user?.bed) {
            //setting bed vacant
            if (userFields.bed == 0) {
              const updatedBed = {
                occupant_id: userFields.bed,
              };

              patchBed(updatedBed, userFields.previousBed)
                .then((res: any) => {})
                .catch((err: any) => {
                  setErrMsg(err?.response?.data?.msg);
                });
            } else if (userFields.bed) {
              const updatedBed = {
                occupant_id: user?.id,
                occupant_type: "guardian",
              };

              patchBed(updatedBed, userFields.bed)
                .then((res: any) => {})
                .catch((err: any) => {
                  setErrMsg(err?.response?.data?.msg);
                });
            }
          }
        }
      }

      //update / create custom field values
      if (customUpdated) {
        custom.map((cust: any) => {
          if (cust?.field_value_id) {
            //patch
            const fieldVal = {
              value: cust?.value,
            };

            patchFieldValue(fieldVal, cust?.field_value_id).catch((err: any) => {
              console.log(err);
              setErrMsg(err?.response?.data?.msg);
            });
          } else if (cust?.value) {
            //post
            const fieldVal = {
              field_id: cust?.field_id,
              person_id: user?.id,
              person_type: "guardian",
              value: cust?.value,
            };

            postFieldValue(fieldVal).catch((err: any) => {
              console.log(err);
              setErrMsg(err?.response?.data?.msg);
            });
          }
        });
      }

      setUserUpdated(false);
      setCustomUpdated(false);
      setSaveIsEnabled(false);
      setUserToSave(false);
      fetchHousehold();
      setShowToast(true);
      const toast = userFields.preferredName
        ? userFields.firstName + " '" + userFields.preferredName + "' " + userFields.lastName
        : userFields.firstName + " " + userFields.lastName;
      setToastText("Primary Adult Updated");
      setToastBody(toast);
    } else {
      validation.errors.map((err: string) => {
        setErrMsg((prev) => (prev ? prev + "  |  " + err : err));
        return true;
      });
    }
  };

  const onClickBan = () => {
    setIsBanning(true);
    setGuardianToBan(userFields.firstName + " " + userFields.lastName);
  };

  const onClickDelete = () => {
    setIsDeleting(true);
  };

  return (
    <>
      {isDeleting ? (
        <ConfirmDelete
          user={user}
          family={guardians}
          setIsDeleting={setIsDeleting}
          fetchHousehold={fetchHousehold}
          resetHouseholdTab={resetHouseholdTab}
          caseStatus={currentCase?.casestatus}
          isLastUser={isLastUser}
          setShowToast={setShowToast}
          setToastText={setToastText}
          setToastBody={setToastBody}
        />
      ) : isBanning ? (
        <ConfirmBan
          user={user}
          previousJob={userFields.previousJob ?? ""}
          isBanned={userFields.isBanned ?? false}
          setIsBanning={setIsBanning}
          userToBan={guardianToBan}
          setUserToBan={setGuardianToBan}
          fetchHousehold={fetchHousehold}
          resetCurrentView={resetHouseholdTab}
          type="Guardian"
          caseStatus={currentCase?.casestatus}
          setShowToast={setShowToast}
          setToastText={setToastText}
          setToastBody={setToastBody}
        />
      ) : isLoading ? (
        <div style={{ minHeight: "400px" }}>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </div>
      ) : (
        <div>
          <Stack gap={3} className="mb-3">
            <Row>
              <Col>
                <h4 className="app-header-text">{"Edit Adult"}</h4>
              </Col>
              <Col className="text-end">
                <Stack className="stack-float" direction="horizontal" gap={3}>
                  {myCase && (
                    <>
                      <Button className="app-danger-color button" onClick={() => onClickDelete()}>
                        Delete
                      </Button>
                      <div className="vr" />
                      <Button className="app-secondary-color button" onClick={() => saveGuardian()} disabled={!saveIsEnabled}>
                        Save
                      </Button>
                      <div className="vr" />
                    </>
                  )}
                  <Button className="app-primary-color button" onClick={() => onClickDoneEdit()}>
                    Cancel
                  </Button>
                </Stack>
              </Col>
            </Row>
            <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
              {errMsg}
            </p>
            <Stack gap={3}>
              <Row className="h-auto">
                <Col xs={4}>
                  <Input
                    label="First Name"
                    type="text"
                    value={userFields.firstName}
                    onUpdate={(e: any) => updateField("firstName", e.target.value)}
                    disabled={myCase ? false : true}
                    required={true}
                  />
                </Col>
                <Col xs={4}>
                  <Input
                    label="Last Name"
                    type="text"
                    value={userFields.lastName}
                    onUpdate={(e: any) => updateField("lastName", e.target.value)}
                    disabled={myCase ? false : true}
                    required={true}
                  />
                </Col>
                <Col xs={4}>
                  <Input
                    label="Preferred Name"
                    type="text"
                    value={userFields.preferredName}
                    onUpdate={(e: any) => updateField("preferredName", e.target.value)}
                    disabled={myCase ? false : true}
                  />
                </Col>
              </Row>
              <Row className="h-auto">
                <Col xs={8}>
                  <Stack gap={3}>
                    <Row>
                      <Col xs={6}>
                        <Input
                          label="Email"
                          type="text"
                          value={userFields.email}
                          onUpdate={(e: any) => updateField("email", e.target.value)}
                          required={userFields.appAccess}
                          disabled={myCase ? false : true}
                        />
                      </Col>
                      <Col xs={6}>
                        <InputPhone
                          label="Phone"
                          value={userFields.phone}
                          onUpdate={(e: any) => updateField("phone", e)}
                          required={userFields.appAccess}
                          disabled={myCase ? false : true}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={6}>
                        <DatePicker
                          label="Intake Date"
                          currentValue={userFields.intakeDate}
                          setCurrentValue={(e: any) => updateField("intakeDate", e)}
                          hasMaxDate={true}
                          disabled={currentCase?.casestatus === "active" && myCase ? false : true}
                        />
                      </Col>
                      {currentCase?.casestatus !== "active" && (
                        <Col xs={6}>
                          <DatePicker
                            label="Exit Date"
                            currentValue={userFields.exitDate}
                            setCurrentValue={(e: any) => updateField("exitDate", e)}
                            hasMaxDate={true}
                            disabled={true}
                          />
                        </Col>
                      )}
                      <Col xs={6} className={currentCase?.casestatus !== "active" ? "mt-3" : ""}>
                        <DatePicker
                          label="Birth Date"
                          currentValue={userFields.birthDate}
                          setCurrentValue={(e: any) => updateField("birthDate", e)}
                          hasMaxDate={true}
                          disabled={myCase ? false : true}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={6}>
                        <SelectObject
                          label="Relationship"
                          value={userFields.relationship}
                          options={relationships}
                          onUpdate={(e: any) => updateField("relationship", e.target.value)}
                          disabled={myCase ? false : true}
                        />
                      </Col>
                      <Col xs={6}>
                        <SelectObject
                          label="Gender"
                          value={userFields.gender}
                          options={genders}
                          onUpdate={(e: any) => updateField("gender", e.target.value)}
                          disabled={myCase ? false : true}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={6}>
                        <SelectObject
                          label="Ethnicity"
                          value={userFields.ethnicity}
                          options={ethnicities}
                          onUpdate={(e: any) => updateField("ethnicity", e.target.value)}
                          disabled={myCase ? false : true}
                        />
                      </Col>
                      <Col xs={6}>
                        <SelectObject
                          label="Race"
                          value={userFields.race}
                          options={races}
                          onUpdate={(e: any) => updateField("race", e.target.value)}
                          disabled={myCase ? false : true}
                        />
                      </Col>
                    </Row>
                  </Stack>
                </Col>
                <Col xs={4}>
                  <CaptureProfilePic
                    userId={user?.id}
                    userProfileId={user?.profile_id}
                    userName={userFields.firstName + " " + userFields.lastName}
                    userType="guardian"
                    myCase={myCase}
                    profileImage={profileImage}
                    setProfileImage={setProfileImage}
                    setErrMsg={setErrMsg}
                    setShowToast={setShowToast}
                    setToastText={setToastText}
                    setToastBody={setToastBody}
                  />
                </Col>
              </Row>
              {(programs.length > 0 || jobs.length > 0) && (
                <Row className="h-auto">
                  {programs.length > 0 && (
                    <Col xs={4}>
                      <SelectObjectTypeahead
                        label="Program"
                        value={userFields.program}
                        options={programs}
                        onUpdate={(e: any) => updateProgram(e)}
                        disabled={userFields.isBanned ? true : myCase ? false : true}
                      />
                    </Col>
                  )}
                  {levels.length > 0 && (
                    <Col xs={4}>
                      <div>
                        <SelectObjectTypeahead
                          label="Program Level"
                          value={userFields.level}
                          options={levels}
                          onUpdate={(e: any) => updateLevel(e)}
                          disabled={userFields.isBanned ? true : myCase ? false : true}
                        />
                      </div>
                    </Col>
                  )}
                  {jobs.length > 0 && (
                    <Col xs={4}>
                      <SelectObjectTypeahead
                        label="Job"
                        value={userFields.job}
                        options={jobs}
                        onUpdate={(e: any) => updateJob(e)}
                        disabled={userFields.isBanned ? true : myCase ? false : true}
                      />
                    </Col>
                  )}
                </Row>
              )}
              {rooms.length > 0 && beds.length > 0 && (
                <Row className="h-auto">
                  <Col xs={4}>
                    <SelectObject
                      label="Room"
                      value={userFields.room}
                      options={rooms}
                      onUpdate={(e: any) => updateRoom(e)}
                      disabled={userFields.isBanned ? true : myCase ? false : true}
                    />
                  </Col>
                  <Col xs={4}>
                    <SelectObject
                      label="Bed"
                      value={userFields.bed}
                      options={filteredBeds}
                      onUpdate={(e: any) => updateBed(e)}
                      disabled={userFields.room ? (myCase ? false : true) : true}
                      initialOption="Vacant"
                    />
                  </Col>
                </Row>
              )}
            </Stack>
          </Stack>
          {user?.customfields && user?.customfields?.length > 0 && (
            <Row className="h-auto">
              {user?.customfields?.map((c: any) => (
                <CustomField
                  customField={c}
                  setCustom={setCustom}
                  userId={user?.id}
                  userType={"guardian"}
                  enableSave={() =>
                    validateUser(
                      userFields.firstName,
                      userFields.lastName,
                      userFields.email,
                      userFields.phone,
                      userFields.level,
                      levels.length > 0,
                      userFields.appAccess,
                      "custom",
                    )
                  }
                  setSaveIsEnabled={setSaveIsEnabled}
                  myCase={myCase}
                />
              ))}
            </Row>
          )}
          <Row className="h-auto">
            <Col xs={4} className="text-start">
              <Stack direction="horizontal" gap={3}>
                {(auth?.profile?.role === "org_admin" || auth?.profile?.role === "admin") && (
                  <Button className="app-tertiary-color-inverse button" onClick={() => onClickBan()}>
                    {userFields.isBanned ? "Unban" : "Ban"}
                  </Button>
                )}
                <small className="text-muted">{user?.number_infractions + " Infraction(s)"}</small>
              </Stack>
            </Col>
            <Col xs={4}>
              <FormCheck
                label="Main Contact"
                value={userFields.isPrimary}
                onUpdate={(e: any) => updateIsPrimary()}
                disabled={myCase ? false : true}
              />
              <FormCheck
                label="Mobile App Access"
                value={userFields.appAccess}
                onUpdate={(e: any) => updateField("appAccess", !userFields.appAccess)}
                disabled={myCase ? false : true}
                css={"mb-0"}
              />
              <small>{"(Requires email or phone)"}</small>
              {userFields.appAccessLast && (
                <p className="small-text">{"Last App Usage: " + new Date(userFields.appAccessLast).toLocaleDateString("en-US")}</p>
              )}
            </Col>
          </Row>
        </div>
      )}
    </>
  );
};

export default EditGuardian;
